import { Directive, ElementRef, forwardRef, HostBinding, HostListener, inject, Input } from '@angular/core';

import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import {
  A11yBooleanInput,
  A11yInteractive,
  A11yLabelTarget,
  A11yRole,
  A11ySelectable
} from '../interfaces/a11y.interfaces';
import { A11yKeyboardService } from '../services/a11y-keyboard.service';
import {
  isNativeFocusableControl,
  normalizeAriaValue,
  toBoolean,
  toRequiredAriaBoolean
} from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

@Directive({
  selector: '[khA11ySwitch]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11ySwitchDirective) }]
})
export class A11ySwitchDirective extends A11yLabelDirective implements A11yRole, A11yLabelTarget, A11ySelectable, A11yInteractive {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly keyboardService = inject(A11yKeyboardService);
  private readonly parentSwitch = inject(A11ySwitchDirective, { optional: true, skipSelf: true });

  @Input('khA11ySwitch') public checked: boolean | string | null = null;
  @Input() public disabled: A11yBooleanInput = false;

  @HostBinding('attr.role')
  public get role(): string | null {
    return this.isLabelContext ? null : 'switch';
  }

  @HostBinding('attr.aria-checked')
  public get ariaChecked(): 'true' | 'false' | null {
    if (this.isLabelContext) {
      return null;
    }

    return toRequiredAriaBoolean(this.checked);
  }

  @HostBinding('attr.aria-selected')
  public readonly ariaSelected = null;

  protected override get ariaLabelledByValue(): string | null {
    return normalizeAriaValue(this.a11yLabelledBy) ?? this.parentSwitch?.ariaLabelledBy ?? null;
  }

  protected override get ariaDescribedByValue(): string | null {
    return normalizeAriaValue(this.a11yDescribedBy) ?? this.parentSwitch?.ariaDescribedBy ?? null;
  }

  @HostBinding('attr.tabindex')
  public get tabindex(): string | null {
    if (this.isLabelContext) {
      return null;
    }

    if (this.disabledState) {
      return '-1';
    }

    return isNativeFocusableControl(this.elementRef.nativeElement) ? null : '0';
  }

  @HostBinding('attr.aria-disabled')
  public get ariaDisabled(): string | null {
    if (this.isLabelContext) {
      return null;
    }

    return this.disabledState ? 'true' : null;
  }

  @HostListener('keydown', ['$event'])
  public onA11yKeydown(event: KeyboardEvent): void {
    if (this.isLabelContext) {
      return;
    }

    if (isNativeFocusableControl(this.elementRef.nativeElement)) {
      return;
    }

    this.keyboardService.activateOnEnterOrSpace(event, () => {
      (event.currentTarget as HTMLElement).click();
    });
  }

  private get disabledState(): boolean {
    return toBoolean(this.disabled, true);
  }

  private get isLabelContext(): boolean {
    return this.checked === null || this.checked === '';
  }
}
